"use client";

import { c as _c } from "react/compiler-runtime";
import { RainforestSavePaymentMethod } from "@/components/rainforest/rainforest-save-payment-method";
import { DeclineAudience } from "@/lib/payments/decline-reason";
import { PaymentProcessor } from "@/types/auth";
import { RainforestPaymentMethodConfig } from "@/types/rainforest";
import StripeSaveMethodSurface from "./stripe-save-method-surface";

/**
 * "Add a card on file", whichever processor the clinic is on.
 *
 * The eight surfaces that save a card - the add-payment-method modal, the
 * payment-method picker, the patient-create credit-card field, the form
 * payment block, the self-schedule step, the plan link, the usage modal and the
 * payment-plan dialog - all rendered `RainforestSavePaymentMethod` directly. On
 * a Stripe merchant the server mints a Stripe SetupIntent and those components
 * handed its client secret to Rainforest's embedded form, which is not a
 * refusal anyone sees: it is a card field that never works.
 *
 * The two processors disagree about what "saved" means and the difference is
 * load-bearing:
 *
 * - Rainforest reports the saved method through a DOM event carrying its own
 *   payment-method id, and the caller reads it off the event.
 * - Stripe confirms a SetupIntent and the id arrives as the promise's
 *   `setupIntent.payment_method`.
 *
 * Both are normalised to one callback taking the processor's payment-method id,
 * because that is the only thing every caller does with it: vault it.
 */

interface SaveMethodSurfaceProps {
  processor?: PaymentProcessor;

  /**
   * Who reads a refusal. Defaults to staff, which is right for the console
   * dialogs and wrong for the five surfaces the cardholder uses themselves -
   * the portal's add-card page, self-schedule, the plan link, the embedded
   * store-details form and the form payment block - where staff copy told the
   * patient to "ask the patient to contact their financial institution".
   *
   * Stripe is where this shows: its SetupIntent verifies the card, so a save
   * can be refused. Rainforest's save path never refused one in the sandbox,
   * but it takes the same prop rather than leaving a second default to find.
   */
  audience?: DeclineAudience;

  /**
   * The card-capture session the server minted. It carries both processors'
   * fields - Rainforest's session key and config id, Stripe's publishable key,
   * connected account and client secret - so a call site passes this one object
   * and never names a processor. The loose props below override it for the two
   * pages that hold the fields separately rather than as a config.
   */
  paymentMethodConfig?: RainforestPaymentMethodConfig | null;
  publishableKey?: string | null;
  stripeAccount?: string | null;
  clientSecret?: string | null;

  /**
   * The processor's id for the card just saved. Rainforest reports its own
   * `payment_method_id`; Stripe reports `pm_...`. Callers vault whichever they
   * are given against `payment_method.processor_payment_method_id`.
   */
  onSuccess: (processorPaymentMethodId: string, event?: unknown) => void;
  onFailure: (message: string) => void;
  onValid?: () => void;
  onInvalid?: () => void;
  onMethodUpdated?: (event: unknown) => void;
  className?: string;
  hideButton?: boolean;
  allowedMethods?: string;
}
export default function SaveMethodSurface(t0) {
  const $ = _c(36);
  const {
    processor,
    audience,
    paymentMethodConfig,
    publishableKey,
    stripeAccount,
    clientSecret,
    onSuccess,
    onFailure,
    onValid,
    onInvalid,
    onMethodUpdated,
    className,
    hideButton: t1,
    allowedMethods
  } = t0;
  const hideButton = t1 === undefined ? true : t1;
  const resolvedProcessor = processor ?? paymentMethodConfig?.processor;
  const resolvedKey = publishableKey ?? paymentMethodConfig?.publishableKey;
  const resolvedAccount = stripeAccount ?? paymentMethodConfig?.stripeAccount;
  const resolvedSecret = clientSecret ?? paymentMethodConfig?.clientSecret;
  if (resolvedProcessor === PaymentProcessor.STRIPE) {
    if (!resolvedKey || !resolvedAccount || !resolvedSecret) {
      let t2;
      if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
        t2 = <span className="text-body text-status-error-text">This clinic's card setup is not finished. Please contact support.</span>;
        $[0] = t2;
      } else {
        t2 = $[0];
      }
      let t3;
      if ($[1] !== className) {
        t3 = <p className={className}>{t2}</p>;
        $[1] = className;
        $[2] = t3;
      } else {
        t3 = $[2];
      }
      return t3;
    }
    let t2;
    if ($[3] !== onSuccess) {
      t2 = id => onSuccess(id);
      $[3] = onSuccess;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    let t3;
    if ($[5] !== audience || $[6] !== className || $[7] !== hideButton || $[8] !== onFailure || $[9] !== onInvalid || $[10] !== onValid || $[11] !== resolvedAccount || $[12] !== resolvedKey || $[13] !== resolvedSecret || $[14] !== t2) {
      t3 = <StripeSaveMethodSurface audience={audience} publishableKey={resolvedKey} stripeAccount={resolvedAccount} clientSecret={resolvedSecret} onSuccess={t2} onFailure={onFailure} onValid={onValid} onInvalid={onInvalid} className={className} hideButton={hideButton} />;
      $[5] = audience;
      $[6] = className;
      $[7] = hideButton;
      $[8] = onFailure;
      $[9] = onInvalid;
      $[10] = onValid;
      $[11] = resolvedAccount;
      $[12] = resolvedKey;
      $[13] = resolvedSecret;
      $[14] = t2;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    return t3;
  }
  if (!paymentMethodConfig) {
    let t2;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <span className="text-body text-status-error-text">This clinic's card setup is not finished. Please contact support.</span>;
      $[16] = t2;
    } else {
      t2 = $[16];
    }
    let t3;
    if ($[17] !== className) {
      t3 = <p className={className}>{t2}</p>;
      $[17] = className;
      $[18] = t3;
    } else {
      t3 = $[18];
    }
    return t3;
  }
  let t2;
  if ($[19] !== onSuccess) {
    t2 = event => {
      const id_0 = event?.detail?.[0]?.data?.payment_method_id ?? event?.detail?.[0]?.payment_method_id ?? "";
      onSuccess(id_0, event);
    };
    $[19] = onSuccess;
    $[20] = t2;
  } else {
    t2 = $[20];
  }
  let t3;
  if ($[21] !== onValid) {
    t3 = () => onValid?.();
    $[21] = onValid;
    $[22] = t3;
  } else {
    t3 = $[22];
  }
  let t4;
  if ($[23] !== onInvalid) {
    t4 = () => onInvalid?.();
    $[23] = onInvalid;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  let t5;
  if ($[25] !== allowedMethods || $[26] !== audience || $[27] !== className || $[28] !== hideButton || $[29] !== onFailure || $[30] !== onMethodUpdated || $[31] !== paymentMethodConfig || $[32] !== t2 || $[33] !== t3 || $[34] !== t4) {
    t5 = <RainforestSavePaymentMethod audience={audience} paymentMethodConfig={paymentMethodConfig} onSuccess={t2} onFailure={onFailure} onValid={t3} onInvalid={t4} onMethodUpdated={onMethodUpdated} className={className} hideButton={hideButton} allowedMethods={allowedMethods} />;
    $[25] = allowedMethods;
    $[26] = audience;
    $[27] = className;
    $[28] = hideButton;
    $[29] = onFailure;
    $[30] = onMethodUpdated;
    $[31] = paymentMethodConfig;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
  } else {
    t5 = $[35];
  }
  return t5;
}